import { DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from 'app/global/global.service';
import { SingingPageService } from './signing-page-service';
import { RoleDetail } from './signing-page.dto';
import { DeletedAttachment } from 'app/main/pages/events/editor-emitter.dto';
import { FieldData } from 'app/main/pages/edit-template/edit-template.dto';
import { BehaviorSubject } from 'rxjs';
import { TemplateType } from 'app/main/pages/templates/template.dto';
import { NgbModalRef, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RecipientModalComponent } from 'app/main/pages/general-modals/recipient-modal/recipient-modal.component';
import { InvitationThroughEnum } from 'app/main/pages/general-editor-tabs/recipients-tab/recipient-tab.dto';
import { environment } from 'environments/environment';
import { InvitationTypePipe } from 'app/pipes/invitation-type.pipe';
import { translate } from '@jsverse/transloco';
import { Participants } from '../signing-sidebar/participants/participants.dto';

interface Signable {
    invalidRecipient?: boolean;
    recipient_role: RoleDetail;
    nextOrder?: boolean;
    type: TemplateType;
}

@Directive()
export abstract class AbstractSigningPageComponent {
    public loading = new BehaviorSubject<boolean>(false);
    private recipientModalRef: NgbModalRef | null = null;
    private destroyRef = inject(DestroyRef);

    constructor(
        protected singingPage: SingingPageService,
        protected globalService: GlobalService,
        protected modalService: NgbModal
    ) { }

    signTheDocument(roleId: number, signature: string, signature_type: string, third_party_auth_response, roleDetail: RoleDetail, deleted_attachments: DeletedAttachment[], fields_data: FieldData) {
        const invitationTypePipe = new InvitationTypePipe();
        this.singingPage.prepareSigningData(roleId, { recipient_fields_data: {}, signature: signature, signature_type: signature_type, all_fields_data: fields_data, third_party_auth_response: third_party_auth_response, deleted_attachments: deleted_attachments }, roleDetail.roleable_id).subscribe({
            next: (signable: Signable) => {
                if (signable?.type === TemplateType.EFORM) {
                    if(signable?.invalidRecipient === true){
                        this.addRecipient(signable?.recipient_role).then((result) => {
                            this.singingPage.updateRoleInformation(signable?.recipient_role?.recipient_role_detail?.recipient_role_id, signable?.recipient_role?.recipient_role_detail?.unique_id, result).subscribe({
                                next : () => {
                                    this.globalService.toastrSuccess(translate('Toaster success message for online contract sign (EForm - section) - Rolename has been invited to signaturemethod before the process is completed', {
                                        rolename: signable?.recipient_role.role_name,
                                        signaturemethod: invitationTypePipe.transform(signable?.recipient_role.invitation_type, true)
                                    }));
                                    this.signTheDocument(roleId, signature, signature_type, third_party_auth_response, roleDetail, deleted_attachments, fields_data);
                                },
                                error: (err: string) => {
                                    this.recipientModalRef.close();
                                    this.loading.next(false);
                                    this.globalService.toastrError(err);
                                }
                            });
                        });
                    }else if(signable?.nextOrder === true && signable?.recipient_role.invitation_type === InvitationThroughEnum.SAME_DEVICE && signable?.recipient_role?.recipient_role_detail) {
                        window.location.href = environment.appUrl + `s/${signable?.recipient_role?.recipient_role_detail?.recipient_role_id}/${signable?.recipient_role?.recipient_role_detail?.unique_id}`;
                        setTimeout(() => {
                            window.location.reload();
                        }, 300)
                    }else{
                        window.location.reload();
                    }
                } else {
                    // Reload page to show signed state - post-sign modal will be shown on reload
                    window.location.reload();
                }
            },
            error: (err: string) => {
                this.loading.next(false);
                this.globalService.toastrError(err);
            }
        })
    }

    addRecipient(role:RoleDetail) {
        this.recipientModalRef = this.modalService.open(RecipientModalComponent, {
            backdrop: 'static',
            windowClass: 'edit-recipient-modal'
        });
        this.recipientModalRef.componentInstance._recipientRole = role;
        this.recipientModalRef.componentInstance._updateRecipient = false;
        this.recipientModalRef.componentInstance._navigation = 'recipient';
        this.recipientModalRef.componentInstance._activeIndex = 0;
        this.recipientModalRef.componentInstance.isEditParticipientFromSigningPage = true;
        this.recipientModalRef.componentInstance.isSendForSignature = true;
        return this.recipientModalRef.result;
    }

    /**
     * Gets the names of signers who haven't signed yet from participants data
     * Aligns with backend allRecipientsSigned logic - filters out those who have signed/read-only/approved/forwarded
     * @param participants - The participants data from the participants endpoint
     * @returns Array of full names of pending signers
     */
    protected getPendingSignerNamesFromParticipants(participants: Participants): string[] {
        const recipientRoles = participants?.recipient_roles || [];
        const completedStatuses = ['signed', 'read-only', 'approved', 'forward'];

        return recipientRoles
            .filter(role => {
                const detail = role.recipient_role_detail;
                // Skip senders (is_sender = 1)
                if (detail?.is_sender === 1) return false;
                // Skip read-only authentication types
                if (role.authentication_type === 'read-only') return false;
                // Include if status is NOT in completed statuses
                return !completedStatuses.includes(detail?.status);
            })
            .map(role => {
                const firstName = role.recipient?.firstname || '';
                const lastName = role.recipient?.lastname || '';
                return `${firstName} ${lastName}`.trim();
            })
            .filter(name => name.length > 0);
    }
}
